"use client"
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useMutation, useQueryClient } from "@tanstack/react-query"

import { shopifyTopupOptionsQueryKey } from "@/hooks/queries/shopify-topups"

import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import { purchaseShopifyTopupAction } from "@/server-actions/shopify/topups"
import { useTranslations } from "next-intl"


/**
 * `onSettled` refreshes the billing overview, which is fetched outside React Query —
 * invalidating the options query alone would leave the balance card stale.
 */
export function usePurchaseShopifyTopupMutation(refreshOverview: () => void) {
  const { formatCredits } = useCreditFormatters()

  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsShopifyTopups")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meterHandle }: { meterHandle: string }) => {
      const result = await withQueryHandling(purchaseShopifyTopupAction({ meterHandle }), {
        loading: tUi("addingBalance"),
        success: (value) =>
          "ok" in value && value.status === "credited"
            ? `Added ${formatCredits(value.creditMicrousd)} to your balance`
            : "Top-up received — your balance will update shortly",
      })
      // `ok` is the discriminant: both success variants carry `status`, so testing for
      // that field would treat every completed top-up as a failure.
      if (!("ok" in result)) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shopifyTopupOptionsQueryKey })
      refreshOverview()
    },
  })
}
